/* Point d'entrée unique de l'auto-ouverture du recap « Prise en main ».

   Le dev n'a qu'à appeler `showOnboardingRecapOnLoad()` (ex. au mount d'une page) :
   ça recharge l'état back, pousse la checklist + les modules dans le moteur,
   puis ouvre le recap SI les conditions d'auto-push sont réunies.

   Tout l'état vient du back (getStartOnboarding) ; aucun état local. */

import store from '@/store';
import {
  chaptersToChecklist,
  chaptersToEngineSteps,
  chaptersToModules,
} from './catalog';

/** Pousse dans le moteur la checklist (socle) + les modules (« Pour aller plus
 *  loin »), dérivés des chapitres back. */
export function pushOnboardingToEngine(chapters: any[] = []): void {
  const w = window as any;
  if (typeof w.FygrTour?.setOnboardingSteps === 'function') {
    w.FygrTour.setOnboardingSteps(chaptersToEngineSteps(chapters));
  }
  if (typeof w.FygrTour?.setModules === 'function') {
    w.FygrTour.setModules(chaptersToModules(chapters));
  }
}

/* « Plus tard » (snooze) — met l'auto-ouverture en pause pendant SNOOZE_MS, sans
   désactiver les guides (ça, c'est `dismissed` en back). Faute de champ back
   (`snooze_until`), la date d'expiration est stockée en local : ce n'est PAS de
   l'état de progression (qui reste 100% back), juste un report d'affichage.
   Quand le back exposera un snooze, remplacer ces 2 helpers par lui. */
const SNOOZE_KEY = 'fygr-onboarding-snooze';
export const SNOOZE_MS = 24 * 60 * 60 * 1000; // 24 h

/** Clé scopée à la company : un report ne doit pas suivre un changement de
 *  compte ni un autre utilisateur sur le même navigateur. */
function snoozeKey(): string {
  return `${SNOOZE_KEY}:${localStorage.getItem('FYGR_COMPANY') || 'none'}`;
}

/** Reporte l'auto-ouverture du recap de SNOOZE_MS. */
export function snoozeOnboardingRecap(): void {
  try {
    localStorage.setItem(snoozeKey(), String(Date.now() + SNOOZE_MS));
  } catch {
    /* localStorage indisponible : pas de snooze, tant pis */
  }
}

/** L'auto-ouverture est-elle en pause ? (nettoie la clé une fois expirée) */
export function isOnboardingRecapSnoozed(): boolean {
  try {
    // Nettoie l'ancienne clé non scopée (versions précédentes).
    localStorage.removeItem(SNOOZE_KEY);
    const key = snoozeKey();
    const until = Number(localStorage.getItem(key));
    if (!until) return false;
    if (Date.now() < until) return true;
    localStorage.removeItem(key);
    return false;
  } catch {
    return false;
  }
}

/* Le flux d'onboarding par pop-ups (paywall → « 2ᵉ source de données », orchestré
   dans App.vue) doit être terminé AVANT de proposer le moindre guide : les tutos
   tournent sur des données réelles du dashboard, donc tant qu'une étape est en
   cours ou à venir, il n'y a rien à montrer.

   L'ordre visé est : paywall → pop-up « 2ᵉ source » → tuto. Le dernier maillon est
   piloté par App.vue, qui signale l'ouverture de la pop-up puis appelle
   `resumeOnboardingAfterPopupFlow()` à sa fermeture (quel que soit le bouton). */

/* Pop-up « 2ᵉ source de données » à l'écran. État runtime : il ne concerne que la
   session courante, la décision durable étant portée par le marqueur ci-dessous. */
let dataSourcePopupPending = false;

/** App.vue signale l'ouverture / la fermeture de la pop-up « 2ᵉ source ». */
export function setDataSourcePopupPending(pending: boolean): void {
  dataSourcePopupPending = pending;
  // La pop-up est bien à l'écran : le garde-fou anti-blocage n'a plus lieu d'être.
  if (pending) clearSecondSourceFallback();
}

/* La « 2ᵉ source de données » est proposée UNE FOIS, puis marquée résolue. La
   mémoire est PERSISTÉE en localStorage (scopée company) pour survivre au reload :
   sans ça, la pop-up se re-proposerait à chaque F5 tant que le parcours est actif.
   Limite connue : le marqueur est collé au navigateur (multi-navigateur =
   re-proposée). Le jour où le back expose un flag durable, remplacer ces helpers
   par son état. La règle d'ORDRE (le tuto ne part jamais avant) ne repose PAS sur
   cette mémoire mais sur `isSecondSourceOwed()` ci-dessous. */
const SECOND_SOURCE_KEY = 'fygr-datasource-prompt';

function secondSourceKey(): string {
  return `${SECOND_SOURCE_KEY}:${localStorage.getItem('FYGR_COMPANY') || 'none'}`;
}

/** L'étape « 2ᵉ source » a-t-elle déjà été résolue (cette session ou un reload
 *  précédent) ? */
export function isSecondSourceResolved(): boolean {
  try {
    return !!localStorage.getItem(secondSourceKey());
  } catch {
    // localStorage indisponible : on considère l'étape faite plutôt que de la
    // reproposer en boucle.
    return true;
  }
}

/** Marque l'étape consommée (pop-up proposée puis fermée, acceptée ou non). */
export function markSecondSourceResolved(): void {
  try {
    localStorage.setItem(secondSourceKey(), '1');
  } catch {
    /* localStorage indisponible : isSecondSourceResolved() renverra true de toute façon */
  }
  clearSecondSourceFallback();
}

/**
 * La « 2ᵉ source » est une étape D'INSCRIPTION : elle ne doit être proposée qu'aux
 * comptes dont le parcours d'onboarding est en cours. Sans cette borne, le
 * déclenchement par état la ferait apparaître rétroactivement chez tous les
 * comptes installés (marqueur absent + paywall levé + une banque connectée) —
 * y compris ceux qui ont déjà cinq sources.
 *
 * Parcours en cours = le back propose des chapitres ET l'onboarding n'est pas
 * `dismissed` (refusé par l'utilisateur ou bouclé).
 */
export function isOnboardingJourneyActive(): boolean {
  const onboarding = (store.state as any).onboarding;
  if (onboarding?.guideMeta?.dismissed) return false;
  return chaptersToEngineSteps(onboarding?.chapters || []).length > 0;
}

/**
 * L'étape « 2ᵉ source » est-elle encore DUE à cet utilisateur ? = parcours
 * d'inscription actif (donc `dismissed === false`), au moins une source
 * connectée, paywall franchi, et pas déjà résolue cette session.
 *
 * C'est la GARDE D'ORDRE : tant qu'elle est due, le tuto attend — même si la
 * pop-up n'est pas encore rendue à l'écran. On ne teste donc pas « la pop-up
 * est-elle visible » (ce qui créerait une course pendant qu'elle se monte) mais
 * « est-elle encore attendue ». Zéro dépendance à un délai.
 */
export function isSecondSourceOwed(): boolean {
  if (isSecondSourceResolved()) return false;
  if (!isOnboardingJourneyActive()) return false; // inscriptions + !dismissed
  if (!((store.getters['bank/bankNumber'] || 0) > 0)) return false;
  if (store.state.subscription?.showTrial) return false;
  return true;
}

function isOnboardingPopupFlowComplete(): boolean {
  // 1) « Ajouter une banque » : au moins une source connectée (sinon aucune
  //    donnée → les tutos n'ont rien à montrer).
  if (!((store.getters['bank/bankNumber'] || 0) > 0)) return false;
  // 2) « Paywall » : l'essai ne bloque plus. showTrial retombe à false dès qu'un
  //    abonnement est actif / l'essai est en cours ou résolu.
  if (store.state.subscription?.showTrial) return false;
  // 3) « 2ᵉ source de données » : ni à l'écran (dataSourcePopupPending), ni encore
  //    attendue (isSecondSourceOwed) → le tuto ne part jamais avant elle.
  if (dataSourcePopupPending) return false;
  if (isSecondSourceOwed()) return false;
  return true;
}

/* GARDE-FOU anti-blocage : l'étape peut être « due » mais la pop-up ne jamais se
   monter (état inattendu, condition d'affichage bloquée). Sans filet, le tuto
   resterait suspendu. Quand on retient le tuto pour cette raison, on arme un
   timer : s'il expire alors que l'étape est toujours due et la pop-up absente,
   on résout d'office et on relance l'enchaînement. Le timer n'est QU'un filet —
   l'ordre normal, lui, est garanti par l'événement de fermeture de la pop-up. */
const SECOND_SOURCE_FALLBACK_MS = 6000;
let secondSourceFallbackTimer: ReturnType<typeof setTimeout> | null = null;

function clearSecondSourceFallback(): void {
  if (secondSourceFallbackTimer) {
    clearTimeout(secondSourceFallbackTimer);
    secondSourceFallbackTimer = null;
  }
}

function armSecondSourceFallback(): void {
  if (secondSourceFallbackTimer) return; // déjà armé
  secondSourceFallbackTimer = setTimeout(() => {
    secondSourceFallbackTimer = null;
    // Toujours due et pop-up jamais venue → on débloque le tuto.
    if (isSecondSourceOwed() && !dataSourcePopupPending) {
      markSecondSourceResolved();
      showOnboardingRecapOnLoad({fetch: false});
    }
  }, SECOND_SOURCE_FALLBACK_MS);
}

/**
 * Fin du flux de pop-ups : la « 2ᵉ source de données » vient d'être fermée
 * (validée, ignorée ou fermée à la croix). On marque l'étape résolue, on ferme
 * la porte puis on enchaîne sur le tuto — c'est le maillon paywall → pop-up → tuto.
 */
export async function resumeOnboardingAfterPopupFlow(): Promise<boolean> {
  markSecondSourceResolved();
  setDataSourcePopupPending(false);
  return showOnboardingRecapOnLoad();
}

/**
 * Conditions d'auto-ouverture du recap. Le recap s'ouvre SI TOUTES sont vraies :
 *   - le moteur expose showRecap/setOnboardingSteps ;
 *   - il y a au moins un chapitre socle mappé ;
 *   - aucun tuto n'est demandé via l'URL (?tour=) — sinon c'est autoStart qui joue ;
 *   - aucun tuto n'est déjà actif ;
 *   - l'onboarding n'est pas `dismissed` (back) ;
 *   - l'auto-ouverture n'est pas en pause (« Plus tard » → snooze) ;
 *   - le flux de pop-ups de mise en route est terminé (cf. isOnboardingPopupFlowComplete).
 * Le socle 5/5 n'arrête plus rien : `dismissed` (posé par le back en fin de
 * parcours) est le seul droit d'affichage.
 */
export function shouldShowOnboardingRecap(chapters: any[] = []): boolean {
  const w = window as any;
  if (typeof w.FygrTour?.showRecap !== 'function') return false;
  if (typeof w.FygrTour?.setOnboardingSteps !== 'function') return false;
  const steps = chaptersToEngineSteps(chapters);
  if (!steps.length) return false;
  if (new URLSearchParams(window.location.search).get('tour')) return false;
  if (w.FygrTour.isActive?.()) return false;
  if (store.state.onboarding?.guideMeta?.dismissed) return false;
  if (isOnboardingRecapSnoozed()) return false;
  if (!isOnboardingPopupFlowComplete()) return false;
  // Le socle terminé n'est PLUS une condition d'arrêt : c'est au back de poser
  // `dismissed` quand le parcours est bouclé. À 5/5 on ouvre donc l'écran
  // « Prise en main terminée » + « Pour aller plus loin ».
  return true;
}

/**
 * À appeler pour déclencher l'auto-push. Recharge le back (sauf `fetch:false`),
 * pousse checklist + modules, et ouvre le recap si `shouldShowOnboardingRecap()`.
 * Retourne `true` si le recap a été ouvert.
 */
export async function showOnboardingRecapOnLoad(
  {fetch = true}: {fetch?: boolean} = {},
): Promise<boolean> {
  const w = window as any;
  if (typeof w.FygrTour?.setOnboardingSteps !== 'function') return false;
  if (fetch) {
    try {
      await store.dispatch('onboarding/actionGetStartOnboarding');
    } catch {
      /* on retombe sur l'état courant du store */
    }
  }
  const chapters = store.state.onboarding?.chapters || [];
  // Toujours pousser la checklist/modules (utile même sans ouvrir le recap :
  // la progression doit être à jour pendant les tutos).
  pushOnboardingToEngine(chapters);
  // Parcours ENTIER bouclé (socle + priorités « Pour aller plus loin ») alors que
  // `dismissed` ne l'est pas (typiquement : l'utilisateur vient de RÉ-ACTIVER les
  // guides une fois tout terminé). Sans ça, on ré-ouvrirait « Prise en main
  // terminée » à chaque load alors qu'il n'y a plus rien à faire. On re-pose
  // `dismissed` (comme dismissIfJourneyComplete sur `tour:completed`) et on
  // n'ouvre rien. Tant qu'une priorité reste à faire, on continue en revanche de
  // surfacer le recap + « Pour aller plus loin ».
  // À SUPPRIMER dès que le back pose le flag lui-même à la complétion.
  const journey = chaptersToChecklist(chapters);
  if (journey.length && journey.every((c) => c.completed)) {
    if (!store.state.onboarding?.guideMeta?.dismissed) {
      try {
        await store.dispatch('onboarding/actionSetOnboardingDismissed', {
          dismissed: true,
        });
      } catch (err) {
        console.warn('[onboarding] fin de parcours non persistée (load)', err);
      }
    }
    return false;
  }
  // Étape « 2ᵉ source » due mais pas encore à l'écran : on retient le tuto
  // (l'ordre est garanti) ET on arme le garde-fou anti-blocage au cas où la
  // pop-up ne se monterait jamais.
  if (isSecondSourceOwed() && !dataSourcePopupPending) armSecondSourceFallback();
  if (!shouldShowOnboardingRecap(chapters)) return false;
  const steps = chaptersToEngineSteps(chapters);
  // Compte vierge (aucun chapitre complété) → on lance DIRECTEMENT le 1er tuto
  // (dashboard-welcome) plutôt que le recap. Dès qu'il y a de la progression,
  // on repart du recap.
  const nothingDone = steps.every((s) => !s.completed);
  if (nothingDone && steps[0] && typeof w.FygrTour.start === 'function') {
    w.FygrTour.start(steps[0].id);
    return true;
  }
  w.FygrTour.showRecap();
  return true;
}

export {};
